// 画布拓扑存储：负责节点/边元数据与 Yjs 的同步，组嵌套统一通过 parentId 表达。
import { initCanvasAssetsStoreSync } from '@/entities/node/canvas-asset/model/canvas-assets-store';
import { getProjectId } from '@/entities/project';
import { GROUP_PADDING } from '@/shared/config/group-config';
import {
  ensureSharedMapsAttached,
  synced,
  yCustomNodeDatas,
  ydoc,
  yEdgesMap,
  yNodesMetadata,
  yViewports,
} from '@/shared/config/yjs-config';
import { clearNodeWaveformCache } from '@/shared/lib/audio-wave/audio-waveform-cache.ts';
import {
  type BaseNodeData,
  createNodeData,
  findNodeDefinitionByAsset,
  findNodeDefinitionByFile,
  getNodeDefinition,
  type NodeResourceCreateSource,
} from '@/shared/lib/node-registry';
import { createInitialOpDataForNewNode, getOp } from '@/shared/lib/op-registry';
import type { SerializableValue } from '@/shared/lib/serializable';
import SLog from '@/shared/lib/SLog';
import { getUserID } from '@/shared/lib/uid';
import { getUndoManager } from '@/shared/lib/yjs/undo-manager';
import type { Edge } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { proxy } from 'valtio';
import { bind } from 'valtio-yjs';
import * as Y from 'yjs';

type NodeType = string;

interface NodeMeta {
  id: string;
  type: NodeType;
  position: { x: number; y: number };
  parentId?: string;
}

type ICreateNode = {
  type: NodeType;
  nodeId?: string;
  position?: NodeMeta['position'];
  parentId?: string;
  customData?: any,
  input?: NodeResourceCreateSource,
  skipCreateLog?: boolean,
}

type EdgeMeta = Edge;

const DEFAULT_SOURCE_HANDLE = 'canvas-source-logical';
const DEFAULT_TARGET_HANDLE = 'canvas-target-logical';

export const flowStore = proxy({
  nodes: {} as Record<string, NodeMeta>,
  edges: [] as EdgeMeta[],
});

// Viewport 数据类型
export interface ViewportData {
  x: number;
  y: number;
  zoom: number;
}

const DEFAULT_VIEWPORT: ViewportData = { x: 0, y: 0, zoom: 1 };

// Viewport store
export const viewportStore = proxy({
  viewport: { ...DEFAULT_VIEWPORT } as ViewportData,
});

// 每节点独立 bind，跟踪 unbind 回调
const unbindMap = new Map<string, () => void>();

// Viewport sync 跟踪
let viewportUnobserve: (() => void) | null = null;

const MAX_NODE_ID_RETRY_COUNT = 10;

const GROUP_NODE_TYPE = 'group';
const DEFAULT_NODE_WIDTH = 160;
const DEFAULT_NODE_HEIGHT = 80;
const NODE_POSITION_PRECISION = 100;

// 节点真实渲染的宽、高
const measuredNodeSizes = new Map<string, { width: number; height: number }>();

interface FlowNodeSnapshot {
  id: string;
  type?: string;
  position: { x: number; y: number };
  parentId?: string;
  width?: number | null;
  height?: number | null;
  measured?: { width?: number | null; height?: number | null };
}

interface NodeRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

function readFiniteNumber(value: unknown, fallback = 0) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function isFinitePosition(position: unknown): position is { x: number; y: number } {
  if (!position || typeof position !== 'object') {
    return false;
  }

  const point = position as { x?: unknown; y?: unknown };
  return (
    typeof point.x === 'number' &&
    Number.isFinite(point.x) &&
    typeof point.y === 'number' &&
    Number.isFinite(point.y)
  );
}

function roundNodeCoordinate(value: number) {
  const rounded = Math.round(value * NODE_POSITION_PRECISION) / NODE_POSITION_PRECISION;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function toStoredNodePosition(position: { x: number; y: number }) {
  if (!isFinitePosition(position)) {
    // 防止 NaN/undefined 坐标写入 Yjs；一旦进入协作状态，会导致 React Flow 边路径渲染成 CNaN。
    throw new Error('节点坐标必须是有限数字');
  }

  return {
    x: roundNodeCoordinate(position.x),
    y: roundNodeCoordinate(position.y),
  };
}

function readFinitePositiveNumber(value: unknown, fallback = 1) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

export function normalizeViewportData(value: unknown): ViewportData {
  if (value instanceof Y.Map) {
    // 兼容历史脏 viewport：viewport 一旦出现 NaN/null，会让缩放百分比、背景 pattern 和边路径一起变成 NaN。
    return {
      x: readFiniteNumber(value.get('x'), DEFAULT_VIEWPORT.x),
      y: readFiniteNumber(value.get('y'), DEFAULT_VIEWPORT.y),
      zoom: readFinitePositiveNumber(value.get('zoom'), DEFAULT_VIEWPORT.zoom),
    };
  }

  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_VIEWPORT };
  }

  const viewport = value as { x?: unknown; y?: unknown; zoom?: unknown };
  return {
    x: readFiniteNumber(viewport.x, DEFAULT_VIEWPORT.x),
    y: readFiniteNumber(viewport.y, DEFAULT_VIEWPORT.y),
    zoom: readFinitePositiveNumber(viewport.zoom, DEFAULT_VIEWPORT.zoom),
  };
}

export function createNodeId() {
  for (let retryCount = 0; retryCount < MAX_NODE_ID_RETRY_COUNT; retryCount += 1) {
    const id = nanoid(8);
    if (!yNodesMetadata.has(id)) {
      return id;
    }
  }

  return crypto.randomUUID();
}

function writeCustomNodeNumber(id: string, key: string, value: number) {
  let yNodeData = yCustomNodeDatas.get(id) as Y.Map<unknown> | undefined;
  if (!yNodeData) {
    yNodeData = new Y.Map();
    yCustomNodeDatas.set(id, yNodeData);
  }
  yNodeData.set(key, value);
}

function getNodeCustomDataSnapshot(id: string) {
  return ((yCustomNodeDatas.get(id) as Y.Map<unknown> | undefined)?.toJSON() ?? {}) as BaseNodeData;
}

function getNodeDisplaySizeFromCustomData(id: string, type?: NodeType) {
  const definition = type ? getNodeDefinition(type) : undefined;
  return definition?.getSize?.(getNodeCustomDataSnapshot(id));
}

function getSnapshotById(nodes: FlowNodeSnapshot[] | undefined) {
  return new Map((nodes ?? []).map((node) => [node.id, node]));
}

function getNodeSizeFromSnapshot(node: FlowNodeSnapshot | undefined, fallbackId: string) {
  const measured = measuredNodeSizes.get(fallbackId);
  const displaySize = getNodeDisplaySizeFromCustomData(fallbackId, node?.type ?? flowStore.nodes[fallbackId]?.type);
  const width = node?.measured?.width
    ?? measured?.width
    ?? node?.width
    ?? displaySize?.width
    ?? DEFAULT_NODE_WIDTH;
  const height = node?.measured?.height
    ?? measured?.height
    ?? node?.height
    ?? displaySize?.height
    ?? DEFAULT_NODE_HEIGHT;

  return {
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
}

export function setMeasuredNodeSize(id: string, width: number, height: number) {
  if (!flowStore.nodes[id]) {
    return;
  }

  measuredNodeSizes.set(id, {
    width: Math.max(1, width),
    height: Math.max(1, height),
  });
}

// 递归计算节点画布绝对坐标；组嵌套后所有打组、解组和入组都依赖这个坐标语义。
function getAbsolutePosition(
  id: string,
  snapshots: Map<string, FlowNodeSnapshot>,
  visiting = new Set<string>(),
): { x: number; y: number } {
  const snapshot = snapshots.get(id);
  const position = snapshot?.position ?? flowStore.nodes[id]?.position ?? { x: 0, y: 0 };
  const parentId = snapshot?.parentId ?? flowStore.nodes[id]?.parentId;

  if (!parentId || visiting.has(id)) {
    return { ...position };
  }

  visiting.add(id);
  const parentPosition = getAbsolutePosition(parentId, snapshots, visiting);
  visiting.delete(id);
  return {
    x: parentPosition.x + position.x,
    y: parentPosition.y + position.y,
  };
}

// 从最新 React Flow 快照优先读取 parentId，快照缺失时回退到协作 store。
function getFlowNodeParentId(id: string, snapshots: Map<string, FlowNodeSnapshot>) {
  return snapshots.get(id)?.parentId ?? flowStore.nodes[id]?.parentId;
}

function hasSelectedAncestor(
  id: string,
  selectedIds: Set<string>,
  snapshots: Map<string, FlowNodeSnapshot>,
) {
  const visited = new Set<string>();
  let parentId = getFlowNodeParentId(id, snapshots);

  while (parentId) {
    if (selectedIds.has(parentId)) return true;
    if (visited.has(parentId)) return false;

    visited.add(parentId);
    parentId = getFlowNodeParentId(parentId, snapshots);
  }

  return false;
}

function resolveCommonParentId(ids: string[], snapshots: Map<string, FlowNodeSnapshot>) {
  if (ids.length === 0) {
    return { ok: false as const };
  }

  const parentId = getFlowNodeParentId(ids[0], snapshots);
  const ok = ids.every((id) => getFlowNodeParentId(id, snapshots) === parentId);
  return ok ? { ok: true as const, parentId } : { ok: false as const };
}

function isDescendantOf(
  nodeId: string,
  ancestorId: string,
  snapshots: Map<string, FlowNodeSnapshot>,
) {
  const visited = new Set<string>();
  let parentId = getFlowNodeParentId(nodeId, snapshots);

  while (parentId) {
    if (parentId === ancestorId) return true;
    if (visited.has(parentId)) return false;

    visited.add(parentId);
    parentId = getFlowNodeParentId(parentId, snapshots);
  }

  return false;
}

function getNodeRect(id: string, snapshots: Map<string, FlowNodeSnapshot>): NodeRect | null {
  const node = snapshots.get(id);
  const meta = flowStore.nodes[id];
  if (!node && !meta) return null;

  const position = getAbsolutePosition(id, snapshots);
  const size = getNodeSizeFromSnapshot(node, id);

  return {
    left: position.x,
    top: position.y,
    right: position.x + size.width,
    bottom: position.y + size.height,
  };
}

function isRectInside(inner: NodeRect, outer: NodeRect) {
  return inner.left >= outer.left && inner.top >= outer.top && inner.right <= outer.right && inner.bottom <= outer.bottom;
}

function setNodePositionInMap(yMap: Y.Map<unknown>, position: { x: number; y: number }) {
  const nextPosition = toStoredNodePosition(position);
  const yPosition = ensurePositionMap(yMap);
  yPosition.set('x', nextPosition.x);
  yPosition.set('y', nextPosition.y);

  return nextPosition;
}

function writeNodePosition(id: string, position: { x: number; y: number }) {
  const node = flowStore.nodes[id];
  const nodeMap = yNodesMetadata.get(id) as Y.Map<unknown> | undefined;
  if (!node || !nodeMap) return null;

  const nextPosition = setNodePositionInMap(nodeMap, position);
  node.position.x = nextPosition.x;
  node.position.y = nextPosition.y;
  return nodeMap;
}

export function setNodePosition(id: string, position: { x: number; y: number }) {
  ydoc.transact(() => {
    writeNodePosition(id, position);
  });
}

export function setNodePositions(entries: { id: string; position: { x: number; y: number } }[]) {
  ydoc.transact(() => {
    for (const { id, position } of entries) {
      writeNodePosition(id, position);
    }
  });
}

function findChildren(groupId: string) {
  return Object.values(flowStore.nodes).filter((node) => node.parentId === groupId);
}

function writeGroupSize(groupId: string, width: number, height: number) {
  const nextWidth = Math.max(1, width);
  const nextHeight = Math.max(1, height);

  writeCustomNodeNumber(groupId, 'width', nextWidth);
  writeCustomNodeNumber(groupId, 'height', nextHeight);
}

export function createEdgeId(connection: Pick<Edge, 'source' | 'target'>) {
  return `e${connection.source}-${connection.target}`;
}

function createEdgeDataForStorage(edge: EdgeMeta) {
  const { id: _, sourceHandle, targetHandle, ...edgeData } = edge;

  return {
    ...edgeData,
    ...(sourceHandle && sourceHandle !== DEFAULT_SOURCE_HANDLE ? { sourceHandle } : {}),
    ...(targetHandle && targetHandle !== DEFAULT_TARGET_HANDLE ? { targetHandle } : {}),
  };
}

function restoreEdgeData(id: string, edgeData: any): Edge {
  return {
    id,
    ...edgeData,
    sourceHandle: edgeData.sourceHandle || DEFAULT_SOURCE_HANDLE,
    targetHandle: edgeData.targetHandle || DEFAULT_TARGET_HANDLE,
  } as Edge;
}

/**
 * 绑定客户端的 viewport 到 yViewports
 */
export function bindViewport() {
  const uid = getUserID();

  // 确保 Y.Map 中有该 uid 的数据
  const existing = yViewports.get(uid);
  const initialViewport = normalizeViewportData(existing);

  // 设置初始值
  viewportStore.viewport = initialViewport;

  // 监听远端 viewport 变化并同步到本地
  const observer = (e: Y.YMapEvent<unknown>) => {
    if (e.keysChanged.has(uid)) {
      const action = e.changes.keys.get(uid);
      if (action?.action === 'add' || action?.action === 'update') {
        const newValue = yViewports.get(uid);
        if (newValue) {
          viewportStore.viewport = normalizeViewportData(newValue);
        }
      }
    }
  };

  yViewports.observe(observer);
  viewportUnobserve = () => yViewports.unobserve(observer);
}

/**
 * 同步 viewport 到 Yjs
 *
 * TODO: 优化 viewport 更新策略
 * - 当前使用 yViewports.set(uid, viewport) 全量更新当前 uid 的数据
 * - 需要确保服务器端支持按 uid 增量更新，避免不必要的网络传输
 * - 可以考虑在 syncViewport 中添加变化检测，只有真正变化时才调用 set
 * - 或者使用 Y.js 的增量更新特性，只传输变化的字段
 */
export function syncViewport(viewport: ViewportData) {
  const uid = getUserID();
  const nextViewport = normalizeViewportData(viewport);
  const roundedViewport = {
    x: roundNodeCoordinate(nextViewport.x),
    y: roundNodeCoordinate(nextViewport.y),
    zoom: nextViewport.zoom,
  };
  ydoc.transact(() => {
    // viewport 是 React Flow 全局 transform，写入前收敛一次，避免 NaN 扩散到整个画布渲染链路。
    yViewports.set(uid, roundedViewport);
  });
}

/**
 * 解绑 viewport
 */
export function unbindViewport() {
  if (viewportUnobserve) {
    viewportUnobserve();
    viewportUnobserve = null;
  }
}

function getNodeType(yMap: Y.Map<unknown>): NodeType {
  return yMap.get('type') as string;
}

function getNodePosition(yMap: Y.Map<unknown>) {
  const rawPosition = yMap.get('position');
  if (rawPosition instanceof Y.Map) {
    return {
      x: readFiniteNumber(rawPosition.get('x')),
      y: readFiniteNumber(rawPosition.get('y')),
    };
  }

  if (rawPosition && typeof rawPosition === 'object') {
    const position = rawPosition as { x?: number; y?: number };
    return {
      x: readFiniteNumber(position.x),
      y: readFiniteNumber(position.y),
    };
  }

  return { x: 0, y: 0 };
}

function normalizeNodePositionInMap(yMap: Y.Map<unknown>) {
  const position = getNodePosition(yMap);

  // bind 会再次从 Y.Map 初始化 proxy；这里先把坏坐标修正，避免 null/NaN 越过读取兼容重新进入画布状态。
  return setNodePositionInMap(yMap, position);
}

function getNodeParentId(yMap: Y.Map<unknown>) {
  const parentId = yMap.get('parentId');
  return typeof parentId === 'string' && parentId.length > 0 ? parentId : undefined;
}

function ensurePositionMap(yMap: Y.Map<unknown>) {
  const existing = yMap.get('position');
  if (existing instanceof Y.Map) {
    return existing as Y.Map<number>;
  }

  const position = new Y.Map<number>();
  yMap.set('position', position);
  return position;
}

function setNodePositionOnStore(id: string, position: { x: number; y: number }) {
  const node = flowStore.nodes[id];
  if (!node) return;

  if (!isFinitePosition(node.position)) {
    node.position = { ...position };
    return;
  }

  node.position.x = position.x;
  node.position.y = position.y;
}

function normalizeBoundNodePosition(id: string, yMap: Y.Map<unknown>) {
  const node = flowStore.nodes[id];
  if (!node) return;

  const rawPosition = yMap.get('position');
  const position = getNodePosition(yMap);
  const needsYjsRepair = !(rawPosition instanceof Y.Map);
  const needsStoreRepair = !isFinitePosition(node.position);

  if (!needsYjsRepair && !needsStoreRepair) {
    return;
  }

  const nextPosition = needsYjsRepair
    ? setNodePositionInMap(yMap, position)
    : toStoredNodePosition(position);

  setNodePositionOnStore(id, nextPosition);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}
// TODO AI 生成代码， 不太理解
function toYValue(value: unknown, seen = new WeakSet<object>()): unknown {
  if (value === undefined) {
    return undefined;
  }

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (value instanceof Y.Text) {
    return value.toString();
  }

  if (value instanceof Y.Array) {
    if (seen.has(value)) {
      throw new Error('包含循环引用，无法写入 Yjs');
    }

    seen.add(value);
    const arr = new Y.Array<unknown>();
    arr.insert(0, value.toArray().map((item) => {
      const val = toYValue(item, seen);
      return val === undefined ? null : val;
    }));
    seen.delete(value);
    return arr;
  }

  if (value instanceof Y.Map) {
    if (seen.has(value)) {
      throw new Error('包含循环引用，无法写入 Yjs');
    }

    seen.add(value);
    const map = new Y.Map<unknown>();
    value.forEach((nestedValue, key) => {
      const val = toYValue(nestedValue, seen);
      if (val !== undefined) {
        map.set(key, val);
      }
    });
    seen.delete(value);
    return map;
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) {
      throw new Error('包含循环引用，无法写入 Yjs');
    }

    seen.add(value);
    const arr = new Y.Array<unknown>();
    arr.insert(0, value.map((item) => {
      const val = toYValue(item, seen);
      return val === undefined ? null : val;
    }));
    seen.delete(value);
    return arr;
  }

  if (!isPlainObject(value)) {
    throw new Error('包含不可序列化的值，无法写入 Yjs');
  }

  if (seen.has(value)) {
    throw new Error('包含循环引用，无法写入 Yjs');
  }

  seen.add(value);
  const map = new Y.Map<unknown>();
  Object.entries(value).forEach(([key, nestedValue]) => {
    const val = toYValue(nestedValue, seen);
    if (val !== undefined) {
      map.set(key, val);
    }
  });
  seen.delete(value);
  return map;
}

function replaceYTextValue(yText: Y.Text, nextValue: string) {
  const apply = () => {
    const currentLength = yText.length;
    if (currentLength > 0) {
      yText.delete(0, currentLength);
    }

    if (nextValue.length > 0) {
      yText.insert(0, nextValue);
    }
  };

  if (yText.doc) {
    yText.doc.transact(apply);
    return;
  }

  apply();
}

function writeCustomFieldValue(
  yNodeDataMap: Y.Map<unknown>,
  key: string,
  value: SerializableValue,
) {
  const existing = yNodeDataMap.get(key);

  if (existing instanceof Y.Text) {
    if (typeof value !== 'string') {
      throw new Error(`节点字段 ${key} 需要字符串值`);
    }

    replaceYTextValue(existing, value);
    return;
  }

  try {
    yNodeDataMap.set(key, toYValue(value));
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`节点字段 ${key} ${error.message}`);
    }

    throw error;
  }
}

function ensureCustomFieldValue(
  yNodeDataMap: Y.Map<unknown>,
  key: string,
  defaultValue: SerializableValue,
) {
  const existing = yNodeDataMap.get(key);

  if (existing === undefined) {
    yNodeDataMap.set(key, toYValue(defaultValue));
    return;
  }

  // 字符串字段默认保持普通值；需要协同文本的字段由 useYText 按需升级。
  if (typeof defaultValue === 'string') {
    return;
  }

  if (Array.isArray(defaultValue)) {
    if (!(existing instanceof Y.Array)) {
      const source = Array.isArray(existing) ? existing : defaultValue;
      yNodeDataMap.set(key, toYValue(source as SerializableValue[]));
    }
    return;
  }

  if (isPlainObject(defaultValue)) {
    if (!(existing instanceof Y.Map) && !(existing instanceof Y.Text)) {
      const source = isPlainObject(existing)
        ? (existing as SerializableValue)
        : defaultValue;
      yNodeDataMap.set(
        key,
        toYValue(source as Record<string, SerializableValue>),
      );
    }
  }
}

/**
 * 确保节点的自定义数据已创建并绑定
 */
function ensureNodeCustomData(id: string, type: NodeType) {
  // 获取或创建节点的 Y.Map
  let yNodeDataMap = yCustomNodeDatas.get(id) as Y.Map<unknown> | undefined;
  if (!yNodeDataMap) {
    yNodeDataMap = new Y.Map();
    yCustomNodeDatas.set(id, yNodeDataMap);
  }

  // 只为缺失的 key 写入默认值，不覆盖远端已有数据
  const initialData = createNodeData(type);
  Object.entries(initialData).forEach(([key, value]) => {
    ensureCustomFieldValue(yNodeDataMap, key, value as SerializableValue);
  });

  return yNodeDataMap;
}

function applyNodeCustomData(
  id: string,
  type: NodeType,
  customData: any,
) {
  const yNodeDataMap = ensureNodeCustomData(id, type);

  Object.entries(customData).forEach(([key, value]) => {
    writeCustomFieldValue(yNodeDataMap, key, value as SerializableValue);
  });

  return yNodeDataMap;
}

function writeNodeMeta(yMap: Y.Map<unknown>, meta: NodeMeta) {
  const position = toStoredNodePosition(meta.position);

  yMap.set('id', meta.id);
  yMap.set('type', meta.type);

  const yPosition = ensurePositionMap(yMap);
  yPosition.set('x', position.x);
  yPosition.set('y', position.y);

  if (meta.parentId) {
    yMap.set('parentId', meta.parentId);
  } else if (yMap.has('parentId')) {
    yMap.delete('parentId');
  }
}

/** 为单个节点建立 proxy ↔ Y.Map 的双向绑定 */
function bindNode(id: string, yMap: Y.Map<unknown>) {
  const type = getNodeType(yMap);
  const position = normalizeNodePositionInMap(yMap);
  ensureNodeCustomData(id, type);
  // 从 Y.Map 恢复初始值到 proxy
  flowStore.nodes[id] = {
    id: yMap.get('id') as string ?? id,
    type,
    position,
    parentId: getNodeParentId(yMap),
  };
  const unbind = bind(
    flowStore.nodes[id] as unknown as Record<string, unknown>,
    yMap,
  );
  const positionRepairObserver = () => {
    normalizeBoundNodePosition(id, yMap);
  };
  yMap.observeDeep(positionRepairObserver);
  unbindMap.set(id, () => {
    yMap.unobserveDeep(positionRepairObserver);
    unbind();
  });
}

/** 从 yNodesMetadata 恢复所有已有节点 */
function restoreNodesFromY() {
  const dirtyKeys: string[] = [];
  yNodesMetadata.forEach((value, key) => {
    if (unbindMap.has(key)) return; // 已绑定过，跳过
    if (!(value instanceof Y.Map)) {
      dirtyKeys.push(key);
      return;
    }
    const yMap = value as Y.Map<unknown>;
    bindNode(key, yMap);
  });

  if (dirtyKeys.length > 0) {
    ydoc.transact(() => {
      dirtyKeys.forEach(key => yNodesMetadata.delete(key));
    });
  }
}

function addNodeMeta(id: string, meta: NodeMeta) {
  ydoc.transact(() => {
    ensureSharedMapsAttached();

    let yMap = yNodesMetadata.get(id) as Y.Map<unknown> | undefined;
    if (!yMap) {
      yMap = new Y.Map();
    }

    // 先填充元数据到 Y.Map，防止观察者在空 Map 时触发
    writeNodeMeta(yMap, meta);
    ensureNodeCustomData(id, meta.type);

    // 元数据填充后再设置到 yNodesMetadata
    if (!yNodesMetadata.has(id)) {
      yNodesMetadata.set(id, yMap);
    }
  });

  if (!unbindMap.has(id)) {
    const yMap = yNodesMetadata.get(id) as Y.Map<unknown> | undefined;
    if (yMap) {
      bindNode(id, yMap);
    }
  }
}

export function setNodeDisplaySize(
  id: string,
  width: number,
  height: number,
  position?: NodeMeta['position'],
) {
  const node = flowStore.nodes[id];
  if (!node) {
    return;
  }

  ydoc.transact(() => {
    writeCustomNodeNumber(id, 'width', Math.max(1, width));
    writeCustomNodeNumber(id, 'height', Math.max(1, height));

    if (!position) {
      return;
    }

    writeNodePosition(id, position);
  });
}

function deleteNodeMetaInsideTransaction(id: string) {
  unbindMap.get(id)?.();
  unbindMap.delete(id);
  measuredNodeSizes.delete(id);
  yCustomNodeDatas.delete(id);
  yNodesMetadata.delete(id);
  delete flowStore.nodes[id];
}

// 解组时只提升一级：内层组的 children 会回到外层组内，而不是直接回到顶层。
function detachChildrenInsideTransaction(groupId: string, snapshots = new Map<string, FlowNodeSnapshot>()) {
  const group = flowStore.nodes[groupId];
  const nextParentId = group?.parentId;
  const nextParentPosition = nextParentId
    ? getAbsolutePosition(nextParentId, snapshots)
    : { x: 0, y: 0 };

  findChildren(groupId).forEach((child) => {
    const absolutePosition = getAbsolutePosition(child.id, snapshots);
    const nextPosition = nextParentId
      ? {
        x: absolutePosition.x - nextParentPosition.x,
        y: absolutePosition.y - nextParentPosition.y,
      }
      : absolutePosition;
    const childMap = writeNodePosition(child.id, nextPosition);
    if (!childMap) return;

    if (nextParentId) {
      childMap.set('parentId', nextParentId);
      child.parentId = nextParentId;
      return;
    }

    childMap.delete('parentId');
    delete child.parentId;
  });
}

export function resizeGroupBounds(
  groupId: string,
  next: {
    position: { x: number; y: number };
    width: number;
    height: number;
    keepChildrenAbsolutePosition: boolean;
  },
) {
  const group = flowStore.nodes[groupId];
  if (!group || group.type !== GROUP_NODE_TYPE) return;

  const nextPosition = toStoredNodePosition(next.position);
  const oldPosition = { ...group.position };
  const delta = {
    x: nextPosition.x - oldPosition.x,
    y: nextPosition.y - oldPosition.y,
  };

  // 在事务前保存子节点的原始位置，避免响应式更新影响计算
  const childrenPositions = new Map(
    findChildren(groupId).map((child) => [child.id, { ...child.position }])
  );

  ydoc.transact(() => {
    const groupMap = yNodesMetadata.get(groupId) as Y.Map<unknown> | undefined;
    if (!groupMap) return;

    const storedGroupPosition = setNodePositionInMap(groupMap, nextPosition);
    group.position.x = storedGroupPosition.x;
    group.position.y = storedGroupPosition.y;

    writeGroupSize(groupId, next.width, next.height);

    if (!next.keepChildrenAbsolutePosition) return;

    findChildren(groupId).forEach((child) => {
      const originalPosition = childrenPositions.get(child.id);
      if (!originalPosition) return;

      writeNodePosition(child.id, {
        x: originalPosition.x - delta.x,
        y: originalPosition.y - delta.y,
      });
    });
  });
}

export function ungroupNodes(groupId: string) {
  const group = flowStore.nodes[groupId];
  if (!group || group.type !== GROUP_NODE_TYPE) return;

  ydoc.transact(() => {
    detachChildrenInsideTransaction(groupId);
    deleteNodeMetaInsideTransaction(groupId);
  });
}

// 创建新的 group。嵌套组语义下，已选 group 作为完整子树进入新 group，不再摊平合并。
export function groupNodes(childIds: string[], snapshots?: FlowNodeSnapshot[]) {
  const snapshotMap = getSnapshotById(snapshots);
  const uniqueIds = Array.from(new Set(childIds)).filter((id) => flowStore.nodes[id]);
  const selectedIdSet = new Set(uniqueIds);
  // 若框选同时包含父组和子节点，只移动父组这棵子树，避免同一节点被重复重挂。
  const targetIds = uniqueIds.filter((id) => !hasSelectedAncestor(id, selectedIdSet, snapshotMap));
  if (targetIds.length < 2) return null;

  const commonParent = resolveCommonParentId(targetIds, snapshotMap);
  if (!commonParent.ok) return null;

  const rects = targetIds
    .map((id) => getNodeRect(id, snapshotMap))
    .filter((rect): rect is NodeRect => rect !== null);

  if (rects.length < 2) return null;

  const bounds = rects.reduce(
    (acc, rect) => ({
      left: Math.min(acc.left, rect.left),
      top: Math.min(acc.top, rect.top),
      right: Math.max(acc.right, rect.right),
      bottom: Math.max(acc.bottom, rect.bottom),
    }),
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity },
  );
  const groupId = createNodeId();
  const groupAbsolutePosition = {
    x: bounds.left - GROUP_PADDING,
    y: bounds.top - GROUP_PADDING,
  };
  const parentAbsolutePosition = commonParent.parentId
    ? getAbsolutePosition(commonParent.parentId, snapshotMap)
    : { x: 0, y: 0 };
  const groupPosition = commonParent.parentId
    ? {
      x: groupAbsolutePosition.x - parentAbsolutePosition.x,
      y: groupAbsolutePosition.y - parentAbsolutePosition.y,
    }
    : groupAbsolutePosition;
  const groupSize = {
    width: bounds.right - bounds.left + GROUP_PADDING * 2,
    height: bounds.bottom - bounds.top + GROUP_PADDING * 2,
  };

  ydoc.transact(() => {
    const groupMap = new Y.Map<unknown>();
    writeNodeMeta(groupMap, {
      id: groupId,
      type: GROUP_NODE_TYPE,
      position: groupPosition,
      parentId: commonParent.parentId,
    });
    yNodesMetadata.set(groupId, groupMap);
    ensureNodeCustomData(groupId, GROUP_NODE_TYPE);
    writeGroupSize(groupId, groupSize.width, groupSize.height);

    targetIds.forEach((id) => {
      const child = flowStore.nodes[id];
      if (!child) return;

      const absolutePosition = getAbsolutePosition(id, snapshotMap);
      const relativePosition = {
        x: absolutePosition.x - groupAbsolutePosition.x,
        y: absolutePosition.y - groupAbsolutePosition.y,
      };
      const childMap = writeNodePosition(id, relativePosition);
      if (!childMap) return;

      childMap.set('parentId', groupId);
      child.parentId = groupId;
    });
  });

  const groupMap = yNodesMetadata.get(groupId) as Y.Map<unknown> | undefined;
  if (groupMap && !unbindMap.has(groupId)) {
    bindNode(groupId, groupMap);
  }

  return groupId;
}

// 将节点挂入目标 group；允许 group 挂入 group，但禁止形成祖先/子孙循环。
export function attachToGroup(nodeId: string, groupId: string, snapshots?: FlowNodeSnapshot[]) {
  const node = flowStore.nodes[nodeId];
  const group = flowStore.nodes[groupId];
  if (!node || !group || nodeId === groupId || group.type !== GROUP_NODE_TYPE) return;

  const snapshotMap = getSnapshotById(snapshots);
  if (isDescendantOf(groupId, nodeId, snapshotMap)) return;

  const nodeRect = getNodeRect(nodeId, snapshotMap);
  const groupRect = getNodeRect(groupId, snapshotMap);
  if (!nodeRect || !groupRect || !isRectInside(nodeRect, groupRect)) return;

  const nodePosition = getAbsolutePosition(nodeId, snapshotMap);
  const groupPosition = getAbsolutePosition(groupId, snapshotMap);
  const relativePosition = {
    x: nodePosition.x - groupPosition.x,
    y: nodePosition.y - groupPosition.y,
  };

  ydoc.transact(() => {
    const nodeMap = writeNodePosition(nodeId, relativePosition);
    if (!nodeMap) return;

    nodeMap.set('parentId', groupId);
    node.parentId = groupId;
  });
}

// 节点拖出当前 group 后，优先提升到仍然包裹它的祖先 group，否则回到顶层。
export function detachFromGroup(nodeId: string, snapshots?: FlowNodeSnapshot[]) {
  const node = flowStore.nodes[nodeId];
  if (!node?.parentId) return;

  const snapshotMap = getSnapshotById(snapshots);
  const absolutePosition = getAbsolutePosition(nodeId, snapshotMap);
  const nodeRect = getNodeRect(nodeId, snapshotMap);
  let nextParentId = getFlowNodeParentId(node.parentId, snapshotMap);

  while (nextParentId && nodeRect) {
    const parentRect = getNodeRect(nextParentId, snapshotMap);
    if (parentRect && isRectInside(nodeRect, parentRect)) break;

    nextParentId = getFlowNodeParentId(nextParentId, snapshotMap);
  }

  const nextParentPosition = nextParentId
    ? getAbsolutePosition(nextParentId, snapshotMap)
    : { x: 0, y: 0 };
  const nextPosition = nextParentId
    ? {
      x: absolutePosition.x - nextParentPosition.x,
      y: absolutePosition.y - nextParentPosition.y,
    }
    : absolutePosition;

  ydoc.transact(() => {
    const nodeMap = writeNodePosition(nodeId, nextPosition);
    if (!nodeMap) return;

    if (nextParentId) {
      nodeMap.set('parentId', nextParentId);
      node.parentId = nextParentId;
      return;
    }

    nodeMap.delete('parentId');
    delete node.parentId;
  });
}

export function createNode(info: ICreateNode) {
  if (info.input) {
    const { input, parentId, position, nodeId, skipCreateLog } = info;
    const definition = input.kind === 'uploaded-file'
      ? findNodeDefinitionByFile(input.file)
      : findNodeDefinitionByAsset(input.asset)

    if (!definition) return null!

    const customData = definition.createData(input)
    return createNode({
      type: definition.type,
      position,
      parentId,
      customData: customData,
      nodeId: nodeId,
      skipCreateLog,
    })
  }

  const { type, nodeId, customData, parentId, position, skipCreateLog } = info;
  const id = nodeId || createNodeId();
  const nodePosition = position ?? { x: 0, y: 0 };

  addNodeMeta(id, {
    id,
    type,
    position: nodePosition,
    parentId,
  });

  const defaultNodeData = createNodeData(type);
  const finalCustomData = Object.assign(defaultNodeData || {}, customData || {});

  // 自动为所有新节点补全当前 OP 的默认配置参数
  if (finalCustomData.op) {
    const op = finalCustomData.op as string;
    finalCustomData.op_data = finalCustomData.op_data || {};
    if (!finalCustomData.op_data[op]) {
      finalCustomData.op_data[op] = createInitialOpDataForNewNode(finalCustomData as BaseNodeData);
    }
  }

  ydoc.transact(() => {
    applyNodeCustomData(id, type, finalCustomData);
  });

  if (!skipCreateLog) {
    SLog.logEvent('node_create', `project_id-${getProjectId()} node_id-${id} type-${type}`);
  }

  return {
    id,
    type,
  };
}

export function updateNodeCustomData(
  id: string,
  customData: any,
) {
  const node = flowStore.nodes[id];
  if (!node) return false;

  ydoc.transact(() => {
    applyNodeCustomData(id, node.type, customData);
  });

  return true;
}

export function updateNodesCustomData(
  patches: { id: string; customData: any }[],
  itemCallback?: (id: string) => void,
  options: { origin?: unknown } = {},
) {
  const validPatches = patches.filter(({ id }) => Boolean(flowStore.nodes[id]));
  if (validPatches.length === 0) return false;

  ydoc.transact(() => {
    validPatches.forEach(({ id, customData }) => {
      const yNodeMap = yCustomNodeDatas.get(id) as Y.Map<unknown> | undefined;
      if (yNodeMap) {
        Object.entries(customData).forEach(([key, value]) => {
          if (value === undefined) {
            yNodeMap.delete(key);
            return;
          }

          yNodeMap.set(key, value);
        });
        itemCallback?.(id);
      }
    });
  }, options.origin);

  return true;
}

export function removeNodeMeta(id: string) {
  unbindMap.get(id)?.();
  unbindMap.delete(id);
  measuredNodeSizes.delete(id);
  yNodesMetadata.delete(id);
  delete flowStore.nodes[id];
}

export function deleteNode(id: string, projectId?: string) {
  const nodeType = flowStore.nodes[id]?.type;
  if (nodeType === GROUP_NODE_TYPE) {
    ungroupNodes(id);
    return;
  }

  SLog.logEvent('node_delete', `project_id-${getProjectId()} node_id-${id} type-${nodeType}`);

  if (projectId) {
    void clearNodeWaveformCache(projectId, id)
  }

  unbindMap.get(id)?.();
  unbindMap.delete(id);
  measuredNodeSizes.delete(id);
  ydoc.transact(() => {
    yCustomNodeDatas.delete(id);
    yNodesMetadata.delete(id);
  });
  delete flowStore.nodes[id];
}

function collectStoredNodeWithChildren(
  nodeId: string,
  collectedNodeIds: Set<string>,
) {
  if (collectedNodeIds.has(nodeId) || !flowStore.nodes[nodeId]) {
    return;
  }

  collectedNodeIds.add(nodeId);

  Object.values(flowStore.nodes)
    .filter((node) => node.parentId === nodeId)
    .forEach((child) => collectStoredNodeWithChildren(child.id, collectedNodeIds));
}

function logNodeDelete(id: string, _projectId?: string) {
  const nodeType = flowStore.nodes[id]?.type;
  if (nodeType) {
    SLog.logEvent('node_delete', `project_id-${getProjectId()} node_id-${id} type-${nodeType}`);
  }
}

function deleteNodeMetaOnlyInsideTransaction(id: string) {
  unbindMap.get(id)?.();
  unbindMap.delete(id);
  measuredNodeSizes.delete(id);
  yCustomNodeDatas.delete(id);
  yNodesMetadata.delete(id);
  delete flowStore.nodes[id];
}

export function deleteNodesAtomically(
  nodeIds: Iterable<string>,
  edgeIds: Iterable<string> = [],
  projectId?: string,
) {
  const idsToDelete = new Set<string>();
  Array.from(nodeIds).forEach((nodeId) => {
    collectStoredNodeWithChildren(nodeId, idsToDelete);
  });

  const edgeIdsToDelete = Array.from(new Set(edgeIds));
  if (idsToDelete.size === 0 && edgeIdsToDelete.length === 0) {
    return false;
  }

  idsToDelete.forEach((nodeId) => logNodeDelete(nodeId, projectId));

  const undoManager = getUndoManager();
  undoManager?.stopCapturing();
  ydoc.transact(() => {
    edgeIdsToDelete.forEach((edgeId) => {
      cleanupTargetOpDataRefs(edgeId);
      yEdgesMap.delete(edgeId);
    });

    idsToDelete.forEach((nodeId) => {
      deleteNodeMetaOnlyInsideTransaction(nodeId);
    });
  });
  undoManager?.stopCapturing();

  if (projectId) {
    idsToDelete.forEach((nodeId) => {
      void clearNodeWaveformCache(projectId, nodeId);
    });
  }

  return true;
}

export function replaceEdges(edges: EdgeMeta[]) {
  flowStore.edges.splice(0, flowStore.edges.length, ...edges.map((edge) => ({ ...edge })));
}

function isEdgeOrSlotContextRef(value: unknown, edgeId: string) {
  // slot reference 的上下文排序 ID 由 edgeId 派生，删除整条边时需要一并清理。
  return typeof value === 'string' && (value === edgeId || value.startsWith(`${edgeId}:slot:`));
}

function removeEdgeIdFromYMapValue(
  map: Y.Map<unknown>,
  key: string,
  value: unknown,
  edgeId: string,
) {
  if (isEdgeOrSlotContextRef(value, edgeId)) {
    map.delete(key);
    return;
  }

  if (value instanceof Y.Array) {
    const values = value.toArray();
    const nextValues = values.filter((item) => !isEdgeOrSlotContextRef(item, edgeId));
    if (nextValues.length === values.length) return;

    if (nextValues.length === 0) {
      map.delete(key);
      return;
    }

    value.delete(0, value.length);
    value.insert(0, nextValues);
    return;
  }

  if (Array.isArray(value)) {
    const nextValues = value.filter((item) => !isEdgeOrSlotContextRef(item, edgeId));
    if (nextValues.length === value.length) return;

    if (nextValues.length === 0) {
      map.delete(key);
      return;
    }

    map.set(key, toYValue(nextValues as SerializableValue[]));
  }
}

function appendEdgeToTargetCurrentOpRefs(edge: EdgeMeta) {
  // edge 必须有完整 source / target，才能判断这条连接是否能作为目标节点的上下文输入。
  if (!edge.source || !edge.target) return;

  // 连接配置只存在节点定义里，节点类型需要从 Yjs 的节点元数据读取。
  const sourceNodeMeta = yNodesMetadata.get(edge.source) as Y.Map<unknown> | undefined;
  const targetNodeMeta = yNodesMetadata.get(edge.target) as Y.Map<unknown> | undefined;
  if (!sourceNodeMeta || !targetNodeMeta) return;

  // 只在 source 输出类型被 target 接收时记录 refs，避免非法边或过期边污染目标节点 OP 输入。
  const sourceDefinition = getNodeDefinition(getNodeType(sourceNodeMeta));
  const targetNodeType = getNodeType(targetNodeMeta);
  const outputKind = sourceDefinition?.type;
  if (!outputKind) return;

  // refs 是当前 OP 的输入编排状态，所以写入 target.op_data[currentOp].refs。
  const targetNodeDataMap = ensureNodeCustomData(edge.target, targetNodeType);
  const currentOp = targetNodeDataMap.get('op');
  if (typeof currentOp !== 'string' || currentOp.trim().length === 0) return;

  const opDef = getOp(currentOp);
  if (!opDef) return;

  const resolvedAcceptSourceTypes = typeof opDef.acceptSourceTypes === 'function'
    ? opDef.acceptSourceTypes(targetNodeType)
    : opDef.acceptSourceTypes;

  if (!resolvedAcceptSourceTypes?.includes(outputKind)) return;

  const rawOpDataRoot = targetNodeDataMap.get('op_data');
  const opDataRoot: Y.Map<unknown> = rawOpDataRoot instanceof Y.Map ? rawOpDataRoot : new Y.Map<unknown>();
  if (!(rawOpDataRoot instanceof Y.Map)) {
    targetNodeDataMap.set('op_data', opDataRoot);
  }

  const rawCurrentOpData = opDataRoot.get(currentOp);
  const currentOpData: Y.Map<unknown> = rawCurrentOpData instanceof Y.Map ? rawCurrentOpData : new Y.Map<unknown>();
  if (!(rawCurrentOpData instanceof Y.Map)) {
    opDataRoot.set(currentOp, currentOpData);
  }

  const rawRefs = currentOpData.get('refs');
  const refs: Y.Array<string> = rawRefs instanceof Y.Array ? rawRefs : new Y.Array<string>();
  if (!(rawRefs instanceof Y.Array)) {
    // refs 是有序输入编排列表；连线创建时先写 edgeId，slot reference 展开后的 contextId 由 OP 面板补齐。
    currentOpData.set('refs', refs);
  }

  if (!refs.toArray().includes(edge.id)) {
    // 新增连线成功时立即记录顺序，避免等 toolbar 打开后才补 refs 导致顺序丢失。
    refs.push([edge.id]);
  }
}

function cleanupTargetOpDataRefs(edgeId: string) {
  const edgeData = yEdgesMap.get(edgeId) as Partial<EdgeMeta> | undefined;
  const targetNodeId = edgeData?.target;
  if (!targetNodeId) return;

  const yNodeDataMap = yCustomNodeDatas.get(targetNodeId) as Y.Map<unknown> | undefined;
  const opDataRoot = yNodeDataMap?.get('op_data');
  if (!yNodeDataMap || !opDataRoot) return;

  if (opDataRoot instanceof Y.Map) {
    Array.from(opDataRoot.entries()).forEach(([op, opData]) => {
      if (!(opData instanceof Y.Map)) return;

      Array.from(opData.entries()).forEach(([key, value]) => {
        removeEdgeIdFromYMapValue(opData, key, value, edgeId);
      });

      if (opData.size === 0) {
        opDataRoot.delete(op);
      }
    });

    if (opDataRoot.size === 0) {
      yNodeDataMap.delete('op_data');
    }
    return;
  }

  if (isPlainObject(opDataRoot)) {
    const nextOpDataRoot: Record<string, SerializableValue> = {};
    Object.entries(opDataRoot).forEach(([op, opData]) => {
      if (!isPlainObject(opData)) return;

      const nextOpData: Record<string, SerializableValue> = {};
      Object.entries(opData).forEach(([key, value]) => {
        if (value === edgeId) return;

        if (Array.isArray(value)) {
          const nextValues = value.filter((item) => item !== edgeId);
          if (nextValues.length > 0) {
            nextOpData[key] = nextValues as SerializableValue[];
          }
          return;
        }

        nextOpData[key] = value as SerializableValue;
      });

      if (Object.keys(nextOpData).length > 0) {
        nextOpDataRoot[op] = nextOpData;
      }
    });

    if (Object.keys(nextOpDataRoot).length === 0) {
      yNodeDataMap.delete('op_data');
      return;
    }

    yNodeDataMap.set('op_data', toYValue(nextOpDataRoot));
  }
}

/**
 * 增量更新：添加单条边（使用 Yjs Map）
 * 只传输新边数据，不传输整个数组
 */
export function addEdge(edge: EdgeMeta) {
  ensureSharedMapsAttached();
  const edgeData = createEdgeDataForStorage(edge);
  ydoc.transact(() => {
    // 直接操作 Y.Map，只添加新边的 Map 条目
    yEdgesMap.set(edge.id, edgeData);
    // 同一事务内记录 target.op_data[currentOp].refs，保证“边存在”和“OP 输入顺序”同步落到 Yjs。
    appendEdgeToTargetCurrentOpRefs(edge);
  });
}

/**
 * 增量更新：删除单条边（使用 Yjs Map）
 * 只删除指定边，不传输整个数组
 */
export function removeEdge(id: string) {
  ydoc.transact(() => {
    cleanupTargetOpDataRefs(id);
    // 直接操作 Y.Map，只删除指定边的 Map 条目
    yEdgesMap.delete(id);
  });
}

/**
 * 增量更新：替换单条边（使用 Yjs Map）
 * 只更新指定边，不传输整个数组
 */
export function replaceEdge(id: string, edge: EdgeMeta) {
  ensureSharedMapsAttached();
  const edgeData = createEdgeDataForStorage(edge);
  // 直接操作 Y.Map，只更新指定边的 Map 条目
  yEdgesMap.set(id, edgeData);
}

/**
 * 等待服务器同步完成后，再执行 bind。
 * 返回 ready 状态供 App 决定是否渲染 ReactFlow。
 *
 * 注意：内部 useEffect 依赖为空数组，且"空则回填 initData"逻辑只在首次 synced 后判断一次。
 * 这样可以避免后续用户删除最后一个节点时，组件重渲染触发 effect 重新回填 initialNodes 的 bug。
 */
export const useBindGlobal = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let edgesObserver: ((e: Y.YMapEvent<unknown>) => void) | undefined;
    let nodesObserver: ((e: Y.YMapEvent<unknown>) => void) | undefined;
    let canvasAssetsStoreUnsubscribe: (() => void) | undefined;
    let cancelled = false;

    synced.then(() => {
      if (cancelled) return;
      // 1) 恢复所有已有节点
      restoreNodesFromY();

      // 画布素材必须在 ready 前完成同步，确保后续节点挂载时能通过 assetRefs 解析素材。
      canvasAssetsStoreUnsubscribe = initCanvasAssetsStoreSync();

      // 2) 恢复所有已有边（从 yEdgesMap）
      const edges: Edge[] = [];
      yEdgesMap.forEach((value, id) => {
        edges.push(restoreEdgeData(id, value));
      });
      flowStore.edges.splice(0, flowStore.edges.length, ...edges);

      // TODO 为什不使用绑定而是使用yEdgesMap？

      // 4) 监听 yEdgesMap 变化（方案 B：使用 Map）
      edgesObserver = (events: Y.YMapEvent<unknown>) => {
        events.keysChanged.forEach((key) => {
          const action = events.changes.keys.get(key);
          if (action?.action === 'add') {
            const value = yEdgesMap.get(key);
            if (value) {
              const edge = restoreEdgeData(key, value);
              flowStore.edges.push(edge);
            }
          } else if (action?.action === 'delete') {
            const index = flowStore.edges.findIndex((e) => e.id === key);
            if (index !== -1) {
              flowStore.edges.splice(index, 1);
            }
          } else if (action?.action === 'update') {
            const value = yEdgesMap.get(key);
            if (value) {
              const index = flowStore.edges.findIndex((e) => e.id === key);
              if (index !== -1) {
                flowStore.edges[index] = restoreEdgeData(key, value);
              }
            }
          }
        });
      };
      yEdgesMap.observe(edgesObserver);

      // 5) 监听后续远端新增/删除的节点
      nodesObserver = (events: Y.YMapEvent<unknown>) => {
        events.keysChanged.forEach((key) => {
          const action = events.changes.keys.get(key);
          if (action?.action === 'add' && !unbindMap.has(key)) {
            const yMap = yNodesMetadata.get(key) as Y.Map<unknown>;
            if (yMap) bindNode(key, yMap);
          } else if (action?.action === 'delete') {
            unbindMap.get(key)?.();
            unbindMap.delete(key);
            delete flowStore.nodes[key];
          }
        });
      };
      yNodesMetadata.observe(nodesObserver);

      setReady(true);
    });

    return () => {
      cancelled = true;
      if (edgesObserver) yEdgesMap.unobserve(edgesObserver);
      if (nodesObserver) yNodesMetadata.unobserve(nodesObserver);
      canvasAssetsStoreUnsubscribe?.();
    };
  }, []);

  return ready;
};
